import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  Chip,
  Grid,
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  DialogContentText,
  Alert,
} from "@mui/material";
import {
  Close,
  Chair,
  SwapHoriz,
  History,
  CheckCircle,
  Pending,
  Cancel,
  Delete,
} from "@mui/icons-material";
import { switchRequestAPI } from "../services/api";
import toast from "react-hot-toast";

const SwitchRequestModal = ({
  open,
  onClose,
  currentBooking,
  student,
  onSuccess,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [historyTabValue, setHistoryTabValue] = useState(0);
  const [availableOptions, setAvailableOptions] = useState([]);
  const [availableSwitchRequests, setAvailableSwitchRequests] = useState([]);
  const [mutualSwitchRequests, setMutualSwitchRequests] = useState([]);
  const [availableHistory, setAvailableHistory] = useState([]);
  const [mutualHistory, setMutualHistory] = useState([]);
  const [selectedAvailable, setSelectedAvailable] = useState("");
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelRequestId, setCancelRequestId] = useState(null);
  const [cancelRequestType, setCancelRequestType] = useState(""); // "available" or "mutual"
  const [cancelRemarks, setCancelRemarks] = useState("");

  // Library-only
  const currentItemLabel = "Seat";
  const currentItemNumber = currentBooking?.seat_number;
  const currentRoomLabel = currentBooking?.section?.section_name;
  const api = switchRequestAPI.library;

  useEffect(() => {
    if (open) {
      if (tabValue < 4) {
        fetchAvailableOptions();
        fetchSwitchRequests();
      } else {
        fetchSwitchHistory();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tabValue]);

  const fetchAvailableOptions = async () => {
    setLoadingData(true);
    try {
      const response = await api.getAvailableSeats();
      setAvailableOptions(response.data || []);
    } catch (error) {
      console.error("Error fetching available options:", error);
      toast.error("Failed to load available options");
    } finally {
      setLoadingData(false);
    }
  };

  const fetchSwitchRequests = async () => {
    setLoadingRequests(true);
    try {
      const [availableResponse, mutualResponse] = await Promise.all([
        api.getAvailableSwitchRequests({ status: "pending" }),
        api.getMutualSwitchRequests({ status: "pending" }),
      ]);

      setAvailableSwitchRequests(availableResponse.data || []);
      setMutualSwitchRequests(mutualResponse.data || []);
    } catch (error) {
      console.error("Error fetching switch requests:", error);
      toast.error("Failed to load switch requests");
    } finally {
      setLoadingRequests(false);
    }
  };

  const fetchSwitchHistory = async () => {
    setLoadingHistory(true);
    try {
      const [availableHistoryRes, mutualHistoryRes] = await Promise.all([
        api.getAvailableSwitchHistory(),
        api.getMutualSwitchHistory(),
      ]);
      setAvailableHistory(availableHistoryRes.data || []);
      setMutualHistory(mutualHistoryRes.data || []);
    } catch (error) {
      console.error("Error fetching switch history:", error);
      toast.error("Failed to load switch history");
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleAvailableSwitch = async () => {
    if (!selectedAvailable) {
      toast.error(`Please select a ${currentItemLabel.toLowerCase()}`);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        target_seat: parseInt(selectedAvailable),
        remarks: remarks.trim() || undefined,
      };

      const response = await api.createAvailableSwitchRequest(payload);

      toast.success(
        `Available switch request submitted successfully! Request ID: ${response.data.id}`
      );
      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error("Error submitting available switch request:", error);
      toast.error(
        error.response?.data?.detail ||
          error.response?.data?.non_field_errors?.[0] ||
          "Failed to submit switch request"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleMutualSwitch = async () => {
    setLoading(true);
    try {
      const payload = {
        remarks: remarks.trim() || undefined,
      };

      const response = await api.createMutualSwitchRequest(payload);

      toast.success(
        `Mutual switch request submitted successfully! Request ID: ${response.data.id}`
      );
      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error("Error submitting mutual switch request:", error);
      toast.error(
        error.response?.data?.detail ||
          error.response?.data?.non_field_errors?.[0] ||
          "Failed to submit switch request"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = (requestId, requestType) => {
    setCancelRequestId(requestId);
    setCancelRequestType(requestType);
    setCancelRemarks("");
    setCancelDialogOpen(true);
  };

  const confirmCancelRequest = async () => {
    if (!cancelRequestId || !cancelRequestType) return;

    setLoading(true);
    try {
      const payload = {
        remarks: cancelRemarks.trim() || undefined,
      };

      if (cancelRequestType === "available") {
        await api.cancelAvailableSwitchRequest(cancelRequestId, payload);
      } else {
        await api.cancelMutualSwitchRequest(cancelRequestId, payload);
      }

      toast.success("Request cancelled successfully");
      fetchSwitchRequests();
      setCancelDialogOpen(false);
    } catch (error) {
      console.error("Error cancelling request:", error);
      toast.error(error.response?.data?.detail || "Failed to cancel request");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedAvailable("");
    setRemarks("");
    setTabValue(0);
    setAvailableSwitchRequests([]);
    setMutualSwitchRequests([]);
    setAvailableHistory([]);
    setMutualHistory([]);
    onClose();
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return <CheckCircle color="success" />;
      case "pending":
        return <Pending color="warning" />;
      case "rejected":
        return <Cancel color="error" />;
      default:
        return <Pending color="disabled" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "success";
      case "pending":
        return "warning";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderAvailableSwitchRequests = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Request ID</TableCell>
            <TableCell>From Seat</TableCell>
            <TableCell>To Seat</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Remarks</TableCell>
            <TableCell>Submitted At</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {availableSwitchRequests.map((request) => (
            <TableRow key={request.id}>
              <TableCell>{request.id}</TableCell>
              <TableCell>{`Seat ${request.current_seat}`}</TableCell>
              <TableCell>{`Seat ${request.target_seat_number}`}</TableCell>
              <TableCell>
                <Chip
                  label={request.status}
                  color={getStatusColor(request.status)}
                  icon={getStatusIcon(request.status)}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>{request.remarks || "-"}</TableCell>
              <TableCell>{formatDate(request.created_at)}</TableCell>
              <TableCell>
                {request.status === "pending" && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleCancelRequest(request.id, "available")}
                    title="Cancel Request"
                  >
                    <Delete />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderMutualSwitchRequests = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Request ID</TableCell>
            <TableCell>Requester</TableCell>
            <TableCell>Requester Seat</TableCell>
            <TableCell>Partner</TableCell>
            <TableCell>Partner Seat</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Remarks</TableCell>
            <TableCell>Submitted At</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {mutualSwitchRequests.map((request) => (
            <TableRow key={request.id}>
              <TableCell>{request.id}</TableCell>
              <TableCell>{request.student?.name || "Unknown"}</TableCell>
              <TableCell>{`Seat ${request.current_seat}`}</TableCell>
              <TableCell>
                {request.partner_student?.name || "No partner yet"}
              </TableCell>
              <TableCell>{request.partner_seat || "-"}</TableCell>
              <TableCell>
                <Chip
                  label={request.status}
                  color={getStatusColor(request.status)}
                  icon={getStatusIcon(request.status)}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>{request.remarks || "-"}</TableCell>
              <TableCell>{formatDate(request.created_at)}</TableCell>
              <TableCell>
                {request.status === "pending" && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleCancelRequest(request.id, "mutual")}
                    title="Cancel Request"
                  >
                    <Delete />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderAvailableSwitchHistory = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Request ID</TableCell>
            <TableCell>Student</TableCell>
            <TableCell>Previous Seat</TableCell>
            <TableCell>New Seat</TableCell>
            <TableCell>Action</TableCell>
            <TableCell>Remarks</TableCell>
            <TableCell>Changed On</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {availableHistory.map((item) => (
            <TableRow key={item.id}>
              <TableCell>{item.id}</TableCell>
              <TableCell>{item.student}</TableCell>
              <TableCell>{`Seat ${item.previous_seat}`}</TableCell>
              <TableCell>{`Seat ${item.new_seat}`}</TableCell>
              <TableCell>
                <Chip
                  label={item.action}
                  color={getStatusColor(item.action)}
                  icon={getStatusIcon(item.action)}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>{item.remarks || "-"}</TableCell>
              <TableCell>{formatDate(item.changed_on)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderMutualSwitchHistory = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Request ID</TableCell>
            <TableCell>Student A</TableCell>
            <TableCell>Student B</TableCell>
            <TableCell>Old Seat A</TableCell>
            <TableCell>Old Seat B</TableCell>
            <TableCell>New Seat A</TableCell>
            <TableCell>New Seat B</TableCell>
            <TableCell>Action</TableCell>
            <TableCell>Remarks</TableCell>
            <TableCell>Changed On</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {mutualHistory.map((item) => (
            <TableRow key={item.id}>
              <TableCell>{item.id}</TableCell>
              <TableCell>{item.student_a}</TableCell>
              <TableCell>{item.student_b}</TableCell>
              <TableCell>{item.old_seat_a}</TableCell>
              <TableCell>{item.old_seat_b}</TableCell>
              <TableCell>{item.new_seat_a}</TableCell>
              <TableCell>{item.new_seat_b}</TableCell>
              <TableCell>
                <Chip
                  label={item.action}
                  color={getStatusColor(item.action)}
                  icon={getStatusIcon(item.action)}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>{item.remarks || "-"}</TableCell>
              <TableCell>{formatDate(item.changed_on)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, maxHeight: "90vh" },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SwapHoriz color="primary" />
            <Typography variant="h6">Switch Seat</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3 }}>
          {/* Current Booking Info */}
          <Card
            sx={{
              mb: 3,
              bgcolor: "primary.50",
              border: "1px solid",
              borderColor: "primary.200",
            }}
          >
            <CardContent sx={{ py: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Current Seat
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chair color="primary" />
                <Typography variant="body1" fontWeight="medium">
                  {currentRoomLabel ? `${currentRoomLabel} - ` : ""}
                  {currentItemLabel} {currentItemNumber}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Switch Options Tabs */}
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}
          >
            <Tab
              label={`Switch to Available ${currentItemLabel}`}
              icon={<Chair />}
              iconPosition="start"
            />
            <Tab
              label={`Mutual Switch`}
              icon={<SwapHoriz />}
              iconPosition="start"
            />
            <Tab
              label={`Available Switch Requests`}
              icon={<History />}
              iconPosition="start"
            />
            <Tab
              label={`Mutual Switch Requests`}
              icon={<SwapHoriz />}
              iconPosition="start"
            />
            <Tab label={`History`} icon={<History />} iconPosition="start" />
          </Tabs>

          {/* Tab Content */}
          {tabValue === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Available Seats
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select an available seat to switch to. Admin approval required.
              </Typography>

              {loadingData ? (
                <Typography>Loading available options...</Typography>
              ) : availableOptions.length === 0 ? (
                <Typography color="text.secondary">
                  No available seats found.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {availableOptions.map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          cursor: "pointer",
                          "&:hover": { bgcolor: "action.hover" },
                          border:
                            selectedAvailable === item.id.toString()
                              ? "2px solid"
                              : "1px solid",
                          borderColor:
                            selectedAvailable === item.id.toString()
                              ? "primary.main"
                              : "divider",
                        }}
                        onClick={() =>
                          setSelectedAvailable(item.id.toString())
                        }
                      >
                        <CardContent
                          sx={{ p: 2, "&:last-child": { pb: 2 } }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Chair color="success" />
                              <Typography
                                variant="body1"
                                fontWeight="medium"
                              >
                                Seat {item.seat_number}
                              </Typography>
                            </Box>
                          </Box>
                          {item.description && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 1 }}
                            >
                              {item.description}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}

              {/* Remarks Field */}
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Remarks (Optional)"
                placeholder="Any additional comments or preferences..."
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                sx={{ mt: 3 }}
              />
            </Box>
          )}

          {tabValue === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Mutual Switch
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Submit a mutual switch request for your current seat. Admin
                  will match you with another student if available.
                </Typography>
              </Alert>

              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    You don't need to select another student's seat. Submit a
                    mutual switch request and the admin can match you when a
                    compatible request is available.
                  </Typography>
                </CardContent>
              </Card>

              {/* Remarks Field */}
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Remarks (Optional)"
                placeholder="Any preferences or reasons for the switch..."
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </Box>
          )}

          {tabValue === 2 && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Available Switch Requests
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={fetchSwitchRequests}
                  disabled={loadingRequests}
                >
                  Refresh
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                View all submitted available switch requests.
              </Typography>

              {loadingRequests ? (
                <Typography>Loading switch requests...</Typography>
              ) : availableSwitchRequests.length === 0 ? (
                <Typography color="text.secondary">
                  No available switch requests found.
                </Typography>
              ) : (
                renderAvailableSwitchRequests()
              )}
            </Box>
          )}

          {tabValue === 3 && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Mutual Switch Requests
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={fetchSwitchRequests}
                  disabled={loadingRequests}
                >
                  Refresh
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                View all submitted mutual switch requests.
              </Typography>

              {loadingRequests ? (
                <Typography>Loading switch requests...</Typography>
              ) : mutualSwitchRequests.length === 0 ? (
                <Typography color="text.secondary">
                  No mutual switch requests found.
                </Typography>
              ) : (
                renderMutualSwitchRequests()
              )}
            </Box>
          )}

          {tabValue === 4 && (
            <Box>
              <Tabs
                value={historyTabValue}
                onChange={(e, newValue) => setHistoryTabValue(newValue)}
                sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
              >
                <Tab label="Available Switch History" />
                <Tab label="Mutual Switch History" />
              </Tabs>
              {loadingHistory ? (
                <Typography>Loading history...</Typography>
              ) : historyTabValue === 0 ? (
                availableHistory.length === 0 ? (
                  <Typography>No available switch history.</Typography>
                ) : (
                  renderAvailableSwitchHistory()
                )
              ) : mutualHistory.length === 0 ? (
                <Typography>No mutual switch history.</Typography>
              ) : (
                renderMutualSwitchHistory()
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          {tabValue < 2 && (
            <Button
              variant="contained"
              onClick={
                tabValue === 0 ? handleAvailableSwitch : handleMutualSwitch
              }
              disabled={
                loading ||
                loadingData ||
                (tabValue === 0 ? !selectedAvailable : false)
              }
            >
              {loading
                ? "Submitting..."
                : `Submit ${
                    tabValue === 0 ? "Available" : "Mutual"
                  } Switch Request`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Cancel Request Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Switch Request</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to cancel this switch request? This action
            cannot be undone.
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Cancellation Remarks (Optional)"
            placeholder="Reason for cancellation..."
            value={cancelRemarks}
            onChange={(e) => setCancelRemarks(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>
            Keep Request
          </Button>
          <Button
            onClick={confirmCancelRequest}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? "Cancelling..." : "Cancel Request"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SwitchRequestModal;
